import {useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'
import clsx from 'clsx'
import {initValues} from './_module'
import {useFormik} from 'formik'
import * as Yup from 'yup'
import {useAppDispatch, useAppSelector} from '../../../../../redux/hooks'
import Loader from 'app/custom/loader/Loader'
import {
  CreateStudentDocument,
  EditStudentDocument,
  reset,
} from '../../../../../redux/Slice/studentSlice'
import {FileUploader} from 'react-drag-drop-files'
import {showErrorMessage, showSuccessMessage} from '_metronic/helpers/swalHelper'
import Swal from 'sweetalert2'

const StudentDocumentCreate = ({onSetShown, id, student_document_id, reloadData}) => {
  const {t} = useTranslation()
  const [loading, setLoading] = useState(false)
  const [loader, setLoader] = useState(true)
  const dispatch = useAppDispatch()
  const {errors} = useAppSelector((state) => state.campus)
  const fileType = ['JPEG', 'PNG', 'JPG', 'PDF', 'DOCX', 'XLSX']
  const [files, setFiles] = useState<File[]>([])

  // Validation
  const validationSchema = Yup.object().shape({
    title: Yup.string().required(t('VALIDATION.REQUIRED_', {name: t('LABEL.TITLE')})),
  })

  const formik = useFormik({
    initialValues: initValues,
    validationSchema: validationSchema,
    onSubmit: async (values, {setSubmitting}) => {
      if (files.length === 0 && id === 0) {
        showErrorMessage(t('VALIDATION.SELECT_', {name: t('LABEL.DOCUMENT')}))
        return
      }
      setLoading(true)
      const formData = new FormData()
      formData.append('student_document_id', student_document_id)
      formData.append('student_id', id)
      formData.append('title', values.title)
      files.forEach((file) => formData.append('file[]', file))
      dispatch(CreateStudentDocument(formData)).then((res) => {
        if (res.meta.requestStatus === 'fulfilled') {
          showSuccessMessage(
            t('MESSAGES.' + (student_document_id === 0 ? 'CREATED' : 'UPDATED')),
            () => {
              onSetShown(false)
              dispatch(reset())
              reloadData()
            }
          )
        } else showErrorMessage(t('MESSAGES.SERVER_ERROR'))
        setLoading(false)
        setSubmitting(false)
      })
    },
  })

  const handleDrop = (fileList: File[]) => {
    setFiles([...files, ...fileList])
  }

  const handleFileRemove = (index: number) => {
    const newFiles = [...files]
    newFiles.splice(index, 1)
    setFiles(newFiles)
  }

  useEffect(() => {
    if (student_document_id !== 0) {
      dispatch(EditStudentDocument(student_document_id)).then((res: any) => {
        if (res.meta.requestStatus === 'fulfilled') {
          formik.setFieldValue('title', res.payload.title)
          setLoader(false)
        }
      })
    } else setLoader(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      {!loader && (
        <form
          className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
          noValidate
          autoComplete='off'
          onSubmit={formik.handleSubmit}
        >
          {/* First row */}
          <div className='row col-12 shadow-sm m-1 pb-5 pt-5'>
            <div className='row'>
              <div className='col-sm-12'>
                <label className='col-form-label label fw-bolder required'>
                  {t('LABEL.TITLE')}
                </label>
                <input
                  type='text'
                  placeholder={t('LABEL.TITLE')}
                  {...formik.getFieldProps('title')}
                  className={clsx(
                    'form-control form-control-sm',
                    {
                      'is-invalid': (formik.touched.title && formik.errors.title) || errors.title,
                    },
                    {
                      'is-valid': formik.touched.title && !formik.errors.title,
                    }
                  )}
                />
                {formik.touched.title && formik.errors.title && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block fw-bold'>
                      <span role='alert' className='text-danger'>
                        {formik.errors.title}
                      </span>
                    </div>
                  </div>
                )}
              </div>
              <div className='col-sm-12'>
                <label className='col-form-label label fw-bolder required'>
                  {t('LABEL.DOCUMENT')}
                </label>
                <FileUploader
                  multiple={true}
                  handleChange={handleDrop}
                  onDrop={handleDrop}
                  name='file'
                  types={fileType}
                  required={true}
                  style={{maxWidth: '100%'}}
                  hoverTitle={t('VALIDATION.SELECT_', {name: t('LABEL.DOCUMENT')})}
                  label={t('VALIDATION.SELECT_', {name: t('LABEL.DOCUMENT')})}
                  onTypeError={(err: any) =>
                    Swal.fire({
                      title: t('VALIDATION.IMAGE'),
                      text: '',
                      icon: 'error',
                    })
                  }
                  onSizeError={(err: any) =>
                    Swal.fire({
                      title: t('VALIDATION.MAX SIZE'),
                      text: '',
                      icon: 'error',
                    })
                  }
                />
                <ul>
                  {files.map((file: any, index: number) => (
                    <li key={index}>
                      {file.name}
                      <span onClick={() => handleFileRemove(index)}>
                        <i className='fas fa-times fs-4 text-danger mt-2 ms-2 bg-light-dark shadow cursor-pointer'></i>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* second Row */}
          <div className='row col-12  shadow-sm m-1 p-5 mt-5'>
            <div className='fs-6 fw-bold text-gray-700'>
              <button
                type='submit'
                className='btn btn-sm btn-light btn-active-primary fw-bolder'
                disabled={formik.isSubmitting || loading}
              >
                {!loading && (
                  <span className='indicator-label'>
                    <i className='fas fa-save mx-2'></i>
                    {t('BUTTONS.SAVE')}
                  </span>
                )}
                {loading && (
                  <span className='indicator-progress' style={{display: 'block'}}>
                    {t('GLOBAL.LOADING')}
                    <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                  </span>
                )}
              </button>
              <div />
            </div>
          </div>
        </form>
      )}
      {loader && <Loader />}
    </>
  )
}

export default StudentDocumentCreate
